/**
 * LearningProgressCard 組件
 *
 * 顯示學習旅程進度的卡片，包含：
 * - 進行中的學習系列（含進度條）
 * - 所有可選學習系列清單
 * - 開始新系列的操作
 *
 * @module components/LearningProgressCard
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Box,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  IconButton,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  MenuBook as LearningIcon,
  Refresh as RefreshIcon,
  CheckCircle as DoneIcon,
  PlayArrow as StartIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lock as LockIcon,
  EmojiEvents as TrophyIcon,
  DeleteOutline as AbandonIcon,
} from '@mui/icons-material';
import {
  getLearningSeries,
  startLearningSeries,
  abandonLearningSeries,
  LearningStatusResponse,
  LearningSeries,
  LearningProgress,
} from '../api/dailyLife';

// ================================================================
// 常數與型別
// ================================================================

/** 同時進行的學習系列上限 */
const MAX_ACTIVE_SERIES = 2;

interface LearningProgressCardProps {
  autoLoad?: boolean;
  onLoad?: (data: LearningStatusResponse) => void;
}

// ================================================================
// 進度條子元件
// ================================================================

const ProgressItem: React.FC<{
  progress: LearningProgress;
  onAbandon: (seriesId: number) => void;
  abandoning: number | null;
}> = ({ progress, onAbandon, abandoning }) => {
  const pct = progress.total_topics > 0
    ? Math.round((progress.current_step - 1) / progress.total_topics * 100)
    : 0;
  const isCompleted = !!progress.completed_at;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.25 }}>
        <Typography variant="body2" fontWeight={600}>
          {progress.series_name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isCompleted && <TrophyIcon fontSize="small" color="warning" />}
          {isCompleted && <DoneIcon fontSize="small" color="success" />}
          <Chip label={progress.category} size="small" sx={{ fontSize: 12, height: 22 }} />
          {!isCompleted && (
            <Tooltip title="放棄此系列（將清除進度）">
              <span>
                <IconButton
                  size="small"
                  color="default"
                  onClick={() => onAbandon(progress.series_id)}
                  disabled={abandoning !== null}
                  sx={{ p: 0.25, opacity: 0.5, '&:hover': { opacity: 1, color: 'error.main' } }}
                >
                  {abandoning === progress.series_id
                    ? <CircularProgress size={14} />
                    : <AbandonIcon sx={{ fontSize: 18 }} />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
      <LinearProgress
        variant="determinate"
        value={isCompleted ? 100 : pct}
        color={isCompleted ? 'success' : 'primary'}
        sx={{ height: 6, borderRadius: 3, mb: 0.25 }}
      />
      <Typography variant="body2" color="text.secondary">
        {isCompleted
          ? `已完成（${progress.total_days_studied} 天）`
          : `第 ${progress.current_step - 1} / ${progress.total_topics} 課 · ${pct}%`}
      </Typography>
    </Box>
  );
};

// ================================================================
// 所有系列列表子元件
// ================================================================

const SeriesList: React.FC<{
  allSeries: LearningSeries[];
  activeSeries: Set<number>;
  onStart: (seriesId: number) => void;
  starting: number | null;
  atLimit: boolean;
}> = ({ allSeries, activeSeries, onStart, starting, atLimit }) => {
  const categories = Array.from(new Set(allSeries.map((s) => s.category)));

  return (
    <Box>
      {atLimit && (
        <Alert severity="warning" icon={<LockIcon />} sx={{ mb: 1.5, fontSize: 12 }}>
          已達上限（{MAX_ACTIVE_SERIES} 個進行中系列）。請先完成或放棄現有系列，才能開始新的系列。
        </Alert>
      )}
      {categories.map((cat) => (
        <Box key={cat} sx={{ mb: 1.5 }}>
          <Typography variant="body2" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {cat}
          </Typography>
          <List dense disablePadding>
            {allSeries
              .filter((s) => s.category === cat)
              .map((series) => {
                const isActive = activeSeries.has(series.id);
                return (
                  <ListItem key={series.id} disablePadding sx={{ py: 0.25 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2">{series.series_name}</Typography>
                          {series.difficulty && (
                            <Chip label={series.difficulty} size="small" variant="outlined" sx={{ fontSize: 12, height: 20 }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="body2" color="text.secondary">
                          {series.total_topics} 課{series.description ? `・${series.description.slice(0, 30)}` : ''}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      {isActive ? (
                        <Chip label="進行中" size="small" color="primary" sx={{ fontSize: 12 }} />
                      ) : atLimit ? (
                        <Tooltip title={`同時最多只能學習 ${MAX_ACTIVE_SERIES} 個系列`}>
                          <span>
                            <Chip icon={<LockIcon sx={{ fontSize: '14px !important' }} />} label="已達上限" size="small" sx={{ fontSize: 12 }} />
                          </span>
                        </Tooltip>
                      ) : (
                        <Button
                          size="small"
                          startIcon={starting === series.id ? <CircularProgress size={12} /> : <StartIcon />}
                          onClick={() => onStart(series.id)}
                          disabled={starting !== null}
                          sx={{ fontSize: 13, py: 0.5, px: 1.5, minWidth: 0 }}
                        >
                          開始
                        </Button>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
          </List>
        </Box>
      ))}
    </Box>
  );
};

// ================================================================
// 主元件
// ================================================================

const LearningProgressCard: React.FC<LearningProgressCardProps> = ({
  autoLoad = true,
  onLoad,
}) => {
  const [data, setData] = useState<LearningStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const [starting, setStarting] = useState<number | null>(null);
  const [startMsg, setStartMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);
  const [abandoning, setAbandoning] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getLearningSeries();
      setData(result);
      onLoad?.(result);
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ??
        e?.response?.data?.message ??
        '無法載入學習資料，請稍後再試';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [onLoad]);

  useEffect(() => {
    if (autoLoad) loadData();
  }, [autoLoad, loadData]);

  const handleStart = async (seriesId: number) => {
    setStarting(seriesId);
    setStartMsg(null);
    try {
      const res = await startLearningSeries(seriesId);
      setStartMsg({ text: res.message, type: res.success ? 'success' : 'error' });
      if (res.success) await loadData();
    } catch {
      setStartMsg({ text: '開始系列失敗，請稍後再試', type: 'error' });
    } finally {
      setStarting(null);
    }
  };

  const handleAbandon = async (seriesId: number) => {
    setAbandoning(seriesId);
    setStartMsg(null);
    try {
      const res = await abandonLearningSeries(seriesId);
      setStartMsg({ text: res.message, type: res.success ? 'success' : 'error' });
      if (res.success) await loadData();
    } catch {
      setStartMsg({ text: '放棄系列失敗，請稍後再試', type: 'error' });
    } finally {
      setAbandoning(null);
    }
  };

  const activeSeries = new Set(data?.active_progress?.map((p) => p.series_id) ?? []);
  const hasActive = (data?.active_progress?.length ?? 0) > 0;
  const atLimit = activeSeries.size >= MAX_ACTIVE_SERIES;

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        avatar={<LearningIcon color="primary" />}
        title="我的學習旅程"
        titleTypographyProps={{ variant: 'h6' }}
        subheaderTypographyProps={{ variant: 'body2' }}
        subheader={
          hasActive
            ? `進行中 ${data!.active_progress.length} / ${MAX_ACTIVE_SERIES} 個系列${atLimit ? '（已達上限）' : ''}`
            : '選擇最多 2 個系列，每天 10 分鐘持續成長'
        }
        action={
          <IconButton onClick={loadData} disabled={loading} size="small">
            <RefreshIcon />
          </IconButton>
        }
      />

      <CardContent sx={{ pt: 0 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {error && !loading && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {error}
            <Button size="small" onClick={loadData} sx={{ ml: 1 }}>重試</Button>
          </Alert>
        )}

        {startMsg && (
          <Alert severity={startMsg.type} onClose={() => setStartMsg(null)} sx={{ mb: 1 }}>
            {startMsg.text}
          </Alert>
        )}

        {data && !loading && (
          <>
            {/* 進行中的系列 */}
            {hasActive && (
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  進行中
                </Typography>
                {data.active_progress.map((p) => (
                  <ProgressItem key={p.series_id} progress={p} onAbandon={handleAbandon} abandoning={abandoning} />
                ))}
              </Box>
            )}

            {!hasActive && (
              <Alert severity="info" icon={<LearningIcon />} sx={{ mb: 1.5 }}>
                尚未開始任何學習系列。選擇一個感興趣的主題，每天 10 分鐘，持續成長！
              </Alert>
            )}

            <Divider sx={{ mb: 1 }} />

            {/* 探索所有系列 */}
            <Box>
              <Button
                size="small"
                endIcon={showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                onClick={() => setShowAll((v) => !v)}
                sx={{ mb: 1, textTransform: 'none' }}
              >
                {showAll ? '收起' : `探索全部系列（${data.all_series.length}）`}
              </Button>
              <Collapse in={showAll}>
                <SeriesList
                  allSeries={data.all_series}
                  activeSeries={activeSeries}
                  onStart={handleStart}
                  starting={starting}
                  atLimit={atLimit}
                />
              </Collapse>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default LearningProgressCard;
